import * as THREE from 'three';
import type { InputManager } from '../input/InputManager';
import type { GameState } from '../state/GameState';
import type { BuiltLocation } from '../world/Location';
import { PLAYER_RADIUS, type PlayerEntity } from './PlayerEntity';

const GRAVITY = -20;
const JUMP_SPEED = 6.6;
const WALK_SPEED = 4.2;
const SPRINT_SPEED = 7.4;
const FOOTSTEP_DISTANCE = 0.78;

/**
 * Kinematic character controller for the local player: WASD relative to the
 * camera yaw, sprint, jump, gravity, ground snapping against the location's
 * height field, and circle-vs-AABB push-out against prop colliders.
 *
 * Also emits footprint stroke events — footprints ride the exact same
 * stroke/paint pipeline as spray paint, so snowfall buries them too.
 */
export class LocalPlayerController implements PlayerEntity {
  readonly id = 'local';
  readonly position = new THREE.Vector3();
  yaw = 0;
  pitch = 0;
  speed = 0;
  isSpraying = false;
  sprayColor = '#ff5533';

  private velocityY = 0;
  private grounded = true;
  private footstepAccum = 0;
  private footSide = 1;
  /** Fired on each footstep (for audio). */
  onFootstep: (() => void) | null = null;

  constructor(
    private readonly input: InputManager,
    private readonly state: GameState,
    private location: BuiltLocation,
  ) {}

  setLocation(location: BuiltLocation): void {
    this.location = location;
    this.position.copy(location.spawn.pos);
    this.yaw = location.spawn.yaw;
    this.pitch = -0.08;
    this.velocityY = 0;
    this.footstepAccum = 0;
  }

  applyLook(dx: number, dy: number): void {
    const sensitivity = 0.0024;
    this.yaw -= dx * sensitivity;
    this.pitch = THREE.MathUtils.clamp(this.pitch - dy * sensitivity, -1.45, 1.45);
  }

  update(dt: number): void {
    const loc = this.location;
    const move = new THREE.Vector2(
      (this.input.isDown('right') ? 1 : 0) - (this.input.isDown('left') ? 1 : 0),
      (this.input.isDown('back') ? 1 : 0) - (this.input.isDown('forward') ? 1 : 0),
    );
    const targetSpeed =
      move.lengthSq() > 0 ? (this.input.isDown('sprint') ? SPRINT_SPEED : WALK_SPEED) : 0;
    if (move.lengthSq() > 0) move.normalize();

    // Move relative to look yaw.
    const sin = Math.sin(this.yaw);
    const cos = Math.cos(this.yaw);
    const dx = (move.x * cos + move.y * sin) * targetSpeed * dt;
    const dz = (-move.x * sin + move.y * cos) * targetSpeed * dt;
    this.position.x += dx;
    this.position.z += dz;
    this.speed = targetSpeed;

    // Keep inside the playable bounds.
    const b = loc.bounds;
    this.position.x = THREE.MathUtils.clamp(this.position.x, b.minX, b.maxX);
    this.position.z = THREE.MathUtils.clamp(this.position.z, b.minZ, b.maxZ);

    // Push out of prop/building colliders (XZ circle vs AABB).
    this.resolveColliders();

    // Vertical: gravity + jump, snap to the ground height field.
    if (this.grounded && this.input.consumePressed('jump')) {
      this.velocityY = JUMP_SPEED;
      this.grounded = false;
    }
    this.velocityY += GRAVITY * dt;
    this.position.y += this.velocityY * dt;
    const ground = loc.groundHeight(this.position.x, this.position.z);
    if (this.position.y <= ground) {
      this.position.y = ground;
      this.velocityY = 0;
      this.grounded = true;
    } else if (this.position.y > ground + 0.02) {
      this.grounded = false;
    }

    // Footprints while walking on the ground.
    if (this.grounded && targetSpeed > 0) {
      this.footstepAccum += Math.hypot(dx, dz);
      if (this.footstepAccum >= FOOTSTEP_DISTANCE) {
        this.footstepAccum = 0;
        this.emitFootprint();
        this.onFootstep?.();
      }
    }
  }

  /** Positional movement from touch gestures (pan/pinch), in the camera-yaw
   * frame. Respects bounds and colliders; the regular update() keeps handling
   * gravity/ground snap. Footprints accrue exactly like walked distance. */
  nudge(strafe: number, forward: number): void {
    const sin = Math.sin(this.yaw);
    const cos = Math.cos(this.yaw);
    const dx = strafe * cos - forward * sin;
    const dz = -strafe * sin - forward * cos;
    this.position.x += dx;
    this.position.z += dz;
    const b = this.location.bounds;
    this.position.x = THREE.MathUtils.clamp(this.position.x, b.minX, b.maxX);
    this.position.z = THREE.MathUtils.clamp(this.position.z, b.minZ, b.maxZ);
    this.resolveColliders();
    if (this.grounded) {
      this.footstepAccum += Math.hypot(dx, dz);
      if (this.footstepAccum >= FOOTSTEP_DISTANCE) {
        this.footstepAccum = 0;
        this.emitFootprint();
        this.onFootstep?.();
      }
    }
  }

  private resolveColliders(): void {
    const p = this.position;
    for (const box of this.location.colliders) {
      // Ignore boxes entirely above the head or below the feet.
      if (box.min.y > p.y + 1.7 || box.max.y < p.y + 0.15) continue;
      const cx = THREE.MathUtils.clamp(p.x, box.min.x, box.max.x);
      const cz = THREE.MathUtils.clamp(p.z, box.min.z, box.max.z);
      let ddx = p.x - cx;
      let ddz = p.z - cz;
      const distSq = ddx * ddx + ddz * ddz;
      if (distSq >= PLAYER_RADIUS * PLAYER_RADIUS) continue;
      if (distSq < 1e-8) {
        // Center is inside the box: push out along the shallowest face.
        const exitXPos = box.max.x - p.x;
        const exitXNeg = p.x - box.min.x;
        const exitZPos = box.max.z - p.z;
        const exitZNeg = p.z - box.min.z;
        const minExit = Math.min(exitXPos, exitXNeg, exitZPos, exitZNeg);
        if (minExit === exitXPos) p.x = box.max.x + PLAYER_RADIUS;
        else if (minExit === exitXNeg) p.x = box.min.x - PLAYER_RADIUS;
        else if (minExit === exitZPos) p.z = box.max.z + PLAYER_RADIUS;
        else p.z = box.min.z - PLAYER_RADIUS;
      } else {
        const dist = Math.sqrt(distSq);
        ddx /= dist;
        ddz /= dist;
        p.x = cx + ddx * PLAYER_RADIUS;
        p.z = cz + ddz * PLAYER_RADIUS;
      }
    }
  }

  private emitFootprint(): void {
    this.footSide = -this.footSide;
    // Offset alternately left/right of the walking direction.
    const sideX = Math.cos(this.yaw) * 0.16 * this.footSide;
    const sideZ = -Math.sin(this.yaw) * 0.16 * this.footSide;
    const x = this.position.x + sideX;
    const z = this.position.z + sideZ;
    const surfaceId = this.location.groundSurfaceIdAt(x, z);
    if (!surfaceId) return;
    const y = this.location.groundHeight(x, z);
    this.state.strokes.append({
      kind: 'footprint',
      locationId: this.state.locationId,
      surfaceId,
      pos: [x, y, z],
      uv: [0, 0],
      radius: 0.15,
      opacity: 0.3,
      color: '#aebdd2',
      seed: Math.floor(Math.random() * 0x7fffffff),
      t: performance.now() - this.state.epoch,
    });
  }
}
